import { Flex, Image, Text } from "rebass/styled-components";
import styled from "styled-components";
import { FaRegBell } from "react-icons/fa6";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";

import {
  ActivePlaylistClipState,
  FallbackPlaylistTypeState,
  PlaylistClipsState,
} from "MVVM/State/embeddablePlaylist";
import { formatPlaylistClip } from "../../Utilities";
import { useAnalyticsContext } from "MVVM/Hooks/useAnalyticsContext";
import { buildLink } from "MVVM/Utilities/Player";
import { useEffect } from "react";

export const NoClips = () => {
  const backgroundImageUrl = `${process.env.REACT_APP_S3_CLIP_BUCKET}/static/web/iframe/Clip+Image.png`;
  const playlistClips = useRecoilValue(PlaylistClipsState);
  const firstClip = playlistClips?.[0];
  const formattedClip = firstClip ? formatPlaylistClip(firstClip) : null;
  const setActivePlaylistClip = useSetRecoilState(ActivePlaylistClipState);
  const [fallbackPlaylistType, setFallbackPlaylistType] = useRecoilState(
    FallbackPlaylistTypeState,
  );
  const { track } = useAnalyticsContext();
  const ajs_aid = window.rudderanalytics.getAnonymousId();
  const getMessageContent = () => {
    switch (fallbackPlaylistType) {
      case "lastMatch":
        return {
          heading: "Clips from this match aren't ready yet",
          paragraph:
            "Showing clips from the last match between these teams instead. Get notified when clips from this match are ready?",
        };
      case "teamClips":
        return {
          heading: "No clips from previous matchup available",
          paragraph:
            "Showing the best clips from both teams instead. Get notified when clips from this match are ready?",
        };
      case "recentBest":
      default:
        return {
          heading: "No clips available for this match",
          paragraph:
            "Showing the most recent & most popular clips from HLTV instead. Get notified when clips from this match are ready?",
        };
    }
  };

  const messageContent = getMessageContent();

  useEffect(() => {
    track("Component Viewed", {
      locationIdentifier: "pre_match_no_clips",
      location: "embeddable_playlist",
      featureGroup: "embeddable_playlist",
    });
  }, [track]);

  return (
    <Container>
      <BackgroundImage backgroundImageUrl={backgroundImageUrl} />
      <Overlay />

      <Content>
        <StyledIcon
          src={`${process.env.REACT_APP_S3_CLIP_BUCKET}/static/web/iframe/exclamation.png`}
        />
        <Flex flexDirection="column" alignItems="center" style={{ gap: 8 }}>
          <ResponsiveHeading
            variant="headingMedium"
            color="ice"
            textAlign="center"
          >
            {messageContent.heading}
          </ResponsiveHeading>
          <ResponsiveParagraph
            variant="paragraphMedium"
            color="chalk"
            textAlign="center"
          >
            {messageContent.paragraph}
          </ResponsiveParagraph>
        </Flex>

        <ButtonSection>
          <NotifyButton
            onClick={() => {
              track("Button - Click", {
                locationIdentifier: "subscribe_to_hltv_clips_button",
                location: "embeddable_player_overlay",
                featureGroup: "subscribe_to_hltv_clips",
              });
              const link = buildLink({
                url: `${
                  window.location.origin
                }/clip/${formattedClip?.internalId}?utm_source=${
                  formattedClip?.developer?.slug ||
                  new URLSearchParams(window.location.search).get("platform") ||
                  "unknown"
                }&subscribe_to_hltv_clips=true`,
              });
              window.open(link, "_blank");
            }}
          >
            <FaRegBell />
            Notify Me
          </NotifyButton>
          <DismissText
            onClick={() => {
              track("Button - Click", {
                locationIdentifier: "dismiss_no_clips_button",
                location: "embeddable_playlist",
                featureGroup: "subscribe_to_hltv_clips",
              });
              if (playlistClips && playlistClips.length > 0) {
                const formattedClip = formatPlaylistClip(playlistClips[0]);
                setFallbackPlaylistType("");
                setActivePlaylistClip(formattedClip);
              }
            }}
          >
            Dismiss
          </DismissText>
        </ButtonSection>
      </Content>
    </Container>
  );
};

const Content = styled(Flex)`
  align-items: center;
  flex-direction: column;
  gap: 8px;
  height: 100%;
  justify-content: center;
  margin: 0 60px;
  padding: 16px;
  position: relative;
  z-index: 2;

  @media (max-width: 480px) {
    gap: 4px;
    padding: 12px;
    margin: 0 20px;
  }

  @media (min-width: 481px) and (max-width: 768px) {
    gap: 6px;
    padding: 14px;
    margin: 0 30px;
  }

  @media (min-width: 769px) {
    gap: 12px;
    padding: 20px;
    margin: 0 40px;
  }
`;

const StyledIcon = styled(Image)`
  height: 48px;
  width: 48px;

  @media (max-width: 480px) {
    width: 32px;
    height: 32px;
  }

  @media (min-width: 481px) and (max-width: 768px) {
    width: 40px;
    height: 40px;
  }

  @media (min-width: 769px) {
    width: 64px;
    height: 64px;
  }
`;

const Container = styled.div`
  aspect-ratio: 16/9;
  border-radius: 8px;
  box-shadow: 0px 0px 8px 0px #00000066;
  overflow: hidden;
  position: relative;
  width: 100%;
`;

const BackgroundImage = styled.div<{ backgroundImageUrl: string }>`
  background-image: url(${(props) => props.backgroundImageUrl});
  background-position: center;
  background-repeat: no-repeat;
  background-size: cover;
  bottom: -8px;
  filter: blur(8px) brightness(0.3);
  left: -8px;
  position: absolute;
  right: -8px;
  top: -8px;
`;

const Overlay = styled.div`
  background-color: rgba(0, 0, 0, 0.1);
  border-radius: 8px;
  bottom: 0;
  left: 0;
  position: absolute;
  right: 0;
  top: 0;
  z-index: 1;
`;

const ResponsiveHeading = styled(Text)`
  @media (max-width: 480px) {
    font-size: 12px;
    line-height: 16px;
  }

  @media (min-width: 481px) and (max-width: 768px) {
    font-size: 14px;
    line-height: 18px;
  }

  @media (min-width: 769px) {
    font-size: 20px;
    line-height: 24px;
  }
`;

const ResponsiveParagraph = styled(Text)`
  @media (max-width: 480px) {
    font-size: 10px;
    line-height: 14px;
  }

  @media (min-width: 481px) and (max-width: 600px) {
    font-size: 10px;
    line-height: 14px;
  }

  @media (min-width: 601px) and (max-width: 768px) {
    font-size: 12px;
    line-height: 16px;
  }

  @media (min-width: 769px) {
    font-size: 16px;
    line-height: 20px;
  }
`;

const ButtonSection = styled(Flex)`
  align-items: center;
  flex-direction: row;
  gap: 16px;
  margin-top: 16px;

  @media (max-width: 480px) {
    gap: 12px;
    margin-top: 12px;
  }

  @media (min-width: 481px) and (max-width: 768px) {
    gap: 14px;
    margin-top: 14px;
  }

  @media (min-width: 769px) {
    gap: 20px;
    margin-top: 20px;
  }
`;

const NotifyButton = styled.button`
  align-items: center;
  background: ${({ theme }) => theme.colors.envy};
  border-radius: 20px;
  border: none;
  color: white;
  cursor: pointer;
  display: flex;
  font-family: Barlow;
  font-size: 12px;
  font-weight: 500;
  gap: 4px;
  padding: 8px 16px;

  &:hover {
    background: ${({ theme }) => theme.colors.envyAlpha80};
  }

  @media (max-width: 480px) {
    font-size: 10px;
    padding: 6px 12px;
    gap: 3px;
  }

  @media (min-width: 481px) and (max-width: 768px) {
    font-size: 11px;
    padding: 7px 14px;
    gap: 3px;
  }

  @media (min-width: 769px) {
    font-size: 14px;
    padding: 10px 20px;
    gap: 6px;
  }
`;

const DismissText = styled(Text)`
  color: ${({ theme }) => theme.colors.ice};
  cursor: pointer;
  font-family: Barlow;
  font-size: 12px;
  font-weight: 500;
  text-decoration: underline;
  text-decoration-color: ${({ theme }) => theme.colors.envy};
  text-underline-offset: 3px;

  &:hover {
    color: ${({ theme }) => theme.colors.envy};
    transition: color 0.2s ease-in-out;
  }

  @media (max-width: 480px) {
    font-size: 10px;
  }

  @media (min-width: 481px) and (max-width: 768px) {
    font-size: 11px;
  }

  @media (min-width: 769px) {
    font-size: 14px;
  }
`;
